<template>
    <!-- A confirmation that interrupts, so an alertdialog. Focus starts on
         Go Back — the safe choice — and Escape means Go Back too. -->
    <section ref="dialogEl" class="white-card-alert" role="alertdialog" aria-modal="true" :aria-labelledby="titleId" :aria-describedby="bodyId" tabindex="-1">
        <div class="content">
            <button type="button" @click.prevent="close" class="close-button" aria-label="Close">
                <SvgsClose />
            </button>

            <div>
                <h2 :id="titleId" class="headline-2">Permanently Delete Itinerary</h2>

                <p :id="bodyId" class="body-book">This can't be undone.</p>

                <div class="buttons-wrapper">
                    <button type="button" @click.prevent="deleteItineraryHandler" :disabled="!isOnline" :title="!isOnline ? 'Unavailable offline' : undefined" class="delete small-heavy uppercase">Yes, Delete</button>

                    <button ref="goBack" type="button" @click.prevent="close" class="go-back small-heavy uppercase">Go Back</button>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
    // Offline read-only — the store refuses these writes; the control must
    // not look live. See docs/superpowers/specs/2026-09-11-offline-read-only-design.md.
    const { isOnline } = useOnlineStatus();
    // Stores
    import { useUserStore } from '@/stores/user';
    const userStore = useUserStore();

    const { getOhcList, updateOhcList } = useUtils();
    const props = defineProps({
        itinerary: {
            type: Object,
            required: true,
            default: {},
        },
    });
    const { itinerary } = toRefs(props);

    const emit = defineEmits(['closeDelete']);
    function close() {
        emit('closeDelete');
    }

    const dialogEl = ref(null);
    const goBack = ref(null);
    const titleId = useId();
    const bodyId = useId();
    useDialog(dialogEl, { onEscape: close, initialFocus: goBack });

    const { $event } = useNuxtApp();
    function refreshItineraryList() {
        $event('refreshItineraryList');
        $event('itineraryDeleted', itinerary.value.name);
    }
    // Delete itinerary
    // close all modals layers
    // possibly need to force redirect to my itineraries page
    function deleteItineraryHandler() {
        let { ohcList, clone } = getOhcList('ohcItineraries');
        ohcList.forEach((item, index) => {
            if (item.name === itinerary.value.name) {
                clone.splice(index, 1);
            }
        });

        updateOhcList('ohcItineraries', clone);
        userStore.resetItineraries(clone);
        close();
        refreshItineraryList();
    }
</script>

<style lang="scss" scoped>
    .white-card-alert {
        z-index: 400;
        position: fixed;
        background: rgba(0, 0, 0, 0.5);
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 15px;
        // Clear the iPhone home indicator.
        padding-bottom: calc(15px + env(safe-area-inset-bottom));

        // Focus starts on Go Back, not the dialog, but the dialog can still
        // take it on a re-focus.
        &:focus {
            outline: none;
        }

        .content {
            position: relative;
            padding: 50px 30px;
            background-color: $white;
            text-align: center;

            .close-button {
                position: absolute;
                top: 15px;
                right: 15px;
            }

            h2 {
                margin-bottom: 10px;
            }

            p {
                margin-bottom: 25px;
            }

            .buttons-wrapper {
                display: flex;
                flex-direction: column;

                button {
                    width: 100%;
                    max-width: 145px;
                    margin-right: auto;
                    margin-left: auto;
                    padding: 10px;
                    font-family: $title;
                    background-color: $gray-light;
                    color: $black;
                    &.delete {
                        margin-bottom: 20px;
                        background-color: $black;
                        color: $white;
                    }
                }
            }
        }
    }
</style>
